import { useEffect, useState } from 'react'
import { CalendarCheck, Copy, RefreshCw, Link2Off, Check } from 'lucide-react'
import type { CalendarFeedCreated, CalendarFeedStatus } from '@familynest/shared'
import { api } from '../../lib/api'
import { Button } from '../ui'
import { useDialogs } from '../ui/dialogs'

function fmt(iso: string | null): string {
  if (!iso) return 'noch nie'
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso)) + ' Uhr'
}

/**
 * „Kalender abonnieren" (ADR-086): erzeugt einen persönlichen, geheimen
 * iCal-Link, mit dem iPhone-/Google-/Outlook-Kalender die FamilyNest-Termine
 * dieser Person anzeigen (nur lesen). Der Link ist nur direkt nach dem
 * Erzeugen sichtbar — der Server speichert nur den Hash. Danach: neu erzeugen
 * (alter Link stirbt) oder widerrufen.
 */
export function CalendarFeedSection(): React.JSX.Element | null {
  const dialogs = useDialogs()
  const [status, setStatus] = useState<CalendarFeedStatus | null>(null)
  const [fresh, setFresh] = useState<CalendarFeedCreated | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async (): Promise<void> => {
    try {
      setStatus(await api.calendarFeedStatus())
    } catch {
      setStatus(null) // älterer Server ohne Feed → Abschnitt ausblenden
    }
  }
  useEffect(() => {
    void load()
  }, [])

  if (!status) return null

  async function create(): Promise<void> {
    if (status?.active) {
      const ok = await dialogs.confirm({
        title: 'Neuen Link erzeugen?',
        message: 'Der bisherige Link funktioniert danach nicht mehr — Kalender, die ihn abonniert haben, bekommen keine Termine mehr.',
        confirmLabel: 'Neu erzeugen',
        danger: true,
      })
      if (!ok) return
    }
    setBusy(true)
    setError(null)
    try {
      setFresh(await api.createCalendarFeed())
      setCopied(false)
      await load()
    } catch {
      setError('Link konnte nicht erzeugt werden.')
    } finally {
      setBusy(false)
    }
  }

  async function revoke(): Promise<void> {
    const ok = await dialogs.confirm({
      title: 'Abo-Link widerrufen?',
      message: 'Alle Kalender, die den Link nutzen, bekommen ab sofort keine Termine mehr.',
      confirmLabel: 'Widerrufen',
      danger: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      await api.revokeCalendarFeed()
      setFresh(null)
      await load()
    } finally {
      setBusy(false)
    }
  }

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      setError('Kopieren nicht möglich — Link bitte manuell markieren.')
    }
  }

  return (
    <div className="mt-6">
      <h3 className="mb-2 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Kalender abonnieren</h3>
      <div className="rounded-xl border p-3" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-surface)' }}>
        {!status.allowed ? (
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            Deine Termine im Handy-Kalender sehen? Frag Mama oder Papa — sie können das in den Einstellungen freischalten.
          </p>
        ) : (
          <>
            <p className="mb-3 text-sm" style={{ color: 'var(--color-muted)' }}>
              Zeigt deine FamilyNest-Termine im iPhone-, Google- oder Outlook-Kalender an (nur lesen; Müll/Feiertage-Abos sind nicht enthalten).
            </p>

            {fresh && (
              <div className="mb-3 flex flex-col gap-2">
                <input
                  readOnly
                  value={fresh.httpsUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full rounded-lg border px-2 py-1.5 font-mono text-xs"
                  style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-card)', color: 'var(--color-ink)' }}
                  aria-label="Abo-Link"
                />
                <div className="flex flex-wrap gap-2">
                  <a
                    href={fresh.webcalUrl}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium"
                    style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
                  >
                    <CalendarCheck className="size-4" /> Im Kalender öffnen
                  </a>
                  <Button variant="ghost" onClick={() => void copy(fresh.httpsUrl)}>
                    {copied ? <Check className="size-4" /> : <Copy className="size-4" />} {copied ? 'Kopiert' : 'Link kopieren'}
                  </Button>
                </div>
                <p className="text-xs" style={{ color: 'var(--color-danger)' }}>
                  Der Link ist ein Geheimnis: Wer ihn hat, sieht deine Termine. Er wird nur jetzt angezeigt.
                </p>
                <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                  iPhone/Mac: „Im Kalender öffnen". Google: im Browser unter calendar.google.com → „Weitere Kalender +" → „Per URL" den kopierten Link einfügen (Google aktualisiert nur alle paar Stunden).
                </p>
              </div>
            )}

            {status.active && !fresh && (
              <p className="mb-3 text-xs" style={{ color: 'var(--color-muted)' }}>
                Aktiv seit {fmt(status.active.createdAt)} · zuletzt abgerufen: {fmt(status.active.lastUsedAt)}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void create()} disabled={busy} variant={status.active ? 'ghost' : 'primary'}>
                {status.active ? <RefreshCw className="size-4" /> : <CalendarCheck className="size-4" />}
                {status.active ? 'Neuen Link erzeugen' : 'Abo-Link erzeugen'}
              </Button>
              {status.active && (
                <Button variant="ghost" onClick={() => void revoke()} disabled={busy}>
                  <Link2Off className="size-4" /> Widerrufen
                </Button>
              )}
            </div>
          </>
        )}
        {error && <p className="mt-2 text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      </div>
    </div>
  )
}
